import { useCallback, useState } from 'react';
import type { UserEntity } from '@user/UserEntity';
import { FlatButton } from '@framework/ui/atoms';
import { Modal } from '@framework/ui/elements';
import { EditPasswordForm } from '@user/EditPasswordForm';
import { FirebaseAuthenticationClient } from '@framework/firebase/auth';
import { toast } from 'react-hot-toast';
import { trpcClient } from '@framework/trpcClient';
import { useTranslation } from 'react-i18next';

type Props = {
  isOpen: boolean;
  onClose(): void;
  userEntity: UserEntity;
};

export const PasswordCreationModal: React.FC<Props> = ({ isOpen, onClose, userEntity }: Props) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [processing, setProcessing] = useState<boolean>(false);
  const [error, setError] = useState('');

  const handleChangePassword = useCallback((password: string) => {
    setPassword(password);
  }, []);
  const isEmpty = password === '';

  const handleClose = useCallback(() => {
    setProcessing(false);
    onClose();
  }, [onClose]);

  const handleSubmit = useCallback(async () => {
    if (password === '') return;

    setProcessing(true);

    try {
      const { customToken } = await trpcClient.user.auth.createPassword.mutate({ password });

      // パスワード設定(Admin SDK)は Firebase Auth の validSince を進め、現在のセッションの
      // ID/refresh トークンを失効させる。放置すると次のトークンリフレッシュや連携解除(unlink)の
      // タイミングで auth/user-token-expired により強制サインアウトされるため、同一セッションを
      // 引き継いだカスタムトークンで再サインインしてトークンを張り直す。
      // 再サインインの失敗はパスワード設定自体の失敗ではないためエラー表示にはしない。
      if (customToken) {
        await FirebaseAuthenticationClient.signInWithCustomToken(customToken).catch((error) => console.error(error));
      }

      toast.success(t('auth.passwordCreate.modal.successToast'));
      handleClose();
    } catch (error) {
      setError(t('auth.passwordCreate.modal.createError'));
      console.error('Failed create password:', error);
    } finally {
      setProcessing(false);
    }
  }, [handleClose, password, t]);

  return (
    <Modal isOpen={isOpen} onClose={handleClose} className="flex flex-col">
      {/* header */}
      <div className="px-8 pb-4 pt-8 text-2xl font-bold">{t('auth.passwordCreate.modal.title')}</div>
      <hr className="w-full" />

      {/* content */}
      <div className="flex flex-col p-8">
        <div className="pb-2 font-bold">{t('auth.signUpConfirm.emailLabel')}</div>
        <input
          type="text"
          className="cursor-not-allowed rounded-sm border bg-gray-200 p-2"
          defaultValue={userEntity.mailAddress}
          disabled
        />

        <EditPasswordForm autoFocus onChange={handleChangePassword} disabled={processing} />

        {error && <div className="pt-8 text-red-700">{error}</div>}
      </div>

      {/* footer */}
      <hr className="w-full" />
      <div className="flex justify-end px-8 pb-8 pt-4">
        <FlatButton onClick={handleClose}>{t('common.action.close')}</FlatButton>
        <FlatButton
          color="brand"
          onClick={handleSubmit}
          className="ml-2 px-8"
          disabled={isEmpty || processing}
          loading={processing}
        >
          {t('common.action.save')}
        </FlatButton>
      </div>
    </Modal>
  );
};
